import { useEffect, useState } from 'react';
import { api } from './lib/api';
import type { Me } from './lib/types';
import { AdminView } from './views/AdminView';
import { BingoOverlay } from './views/BingoOverlay';
import { ChallengeOverlay } from './views/ChallengeOverlay';
import { Lobbywahl } from './views/Lobbywahl';
import { TwitchFertig } from './views/TwitchFertig';
import { Login } from './views/Login';
import { PasswortFestlegen } from './views/PasswortFestlegen';
import { OwnerView } from './views/PlayerOrOwner';
import { ScoreOverlay } from './views/ScoreOverlay';

/**
 * Wegweiser.
 *
 * Bewusst über die Adresse statt über eine Bibliothek: Es gibt nur eine Handvoll Seiten, und die
 * Overlays müssen sich als schlichte Adresse in OBS eintragen lassen.
 *
 *   /overlay/<schlüssel>              Bingo-Overlay
 *   /overlay/<schlüssel>/scores       Punkte-Overlay
 *   /overlay/<schlüssel>/challenges   Abstimmung und Challenge
 *   /lobby/<id>                     Spielen bzw. verwalten
 *   /                               Anmeldung
 */
export function App() {
  const pfad = window.location.pathname;
  const parameter = new URLSearchParams(window.location.search);

  // Die Overlays laufen ohne Anmeldung – sie werden zuerst behandelt.
  const overlay = /^\/overlay\/([^/]+)(\/scores|\/challenges)?$/.exec(pfad);
  if (overlay) {
    const schlüssel = overlay[1]!;
    if (overlay[2] === '/challenges') return <ChallengeOverlay overlayKey={schlüssel} />;
    if (overlay[2] === '/scores') {
      const ausrichtung = parameter.get('layout') === 'vertikal' ? 'vertikal' : 'horizontal';
      return <ScoreOverlay overlayKey={schlüssel} ausrichtung={ausrichtung} />;
    }
    const team = parameter.get('team');
    return <BingoOverlay overlayKey={schlüssel} {...(team ? { teamId: team } : {})} />;
  }

  /*
   * Die Rückkehr von Twitch. Der Server leitet hierher weiter; wir reichen das Ergebnis an die
   * Lobby durch, in der gerade eingerichtet wurde. Die Lobby steht nicht in der Adresse – sie
   * ergibt sich aus dem Konto, und genau das ist der Grund, warum hier nichts aus der Anfrage
   * übernommen wird.
   */
  if (pfad === '/twitch/fertig') {
    return <TwitchFertig status={parameter.get('status') ?? 'FEHLER'} kanal={parameter.get('kanal')} />;
  }

  return <Angemeldet pfad={pfad} />;
}

function Angemeldet({ pfad }: { pfad: string }) {
  const [me, setMe] = useState<Me | null>(null);
  const [geprüft, setGeprüft] = useState(false);

  useEffect(() => {
    void api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setGeprüft(true));
  }, []);

  if (!geprüft) {
    return (
      <main className="page">
        <p className="muted">einen Moment …</p>
      </main>
    );
  }

  if (!me) return <Login onFertig={() => window.location.reload()} />;

  const abmelden = () => {
    void api.logout().finally(() => {
      window.location.href = '/';
    });
  };

  if (me.mustChangePassword) return <PasswortFestlegen />;

  if (pfad === '/verwaltung') return <AdminView me={me} onLogout={abmelden} />;

  const lobby = /^\/lobby\/([^/]+)$/.exec(pfad)?.[1];
  if (lobby) return <OwnerView lobbyId={lobby} me={me} onLogout={abmelden} />;

  return (
    <Lobbywahl
      me={me}
      onWahl={(id) => window.location.assign(`/lobby/${id}`)}
      onLogout={abmelden}
    />
  );
}
